// Curated SEO-Optimized Party Occasions Directory
// High-intent templates for programmatic landing pages (/occasions/:slug)

export const OCCASION_PAGES = [
  {
    slug: 'neon-30th-birthday',
    occasion: '30th Birthday Party',
    title: 'Neon Nights & Glow Luxe: 30th Birthday Party Planning Guide',
    metaDescription: 'Plan an unforgettable Neon Glow 30th Birthday party with curated moodboards, electric neon palette, signature cocktails, and Amazon decor shopping checklist.',
    badge: 'Trending Vibe 2026',
    vibe: 'Electric Glamour & Club Nostalgia',
    themeName: 'Neon Nights & Glow Luxe',
    archetype: 'Bold & Vibrant',
    estimatedCostRange: '$1,200 – $2,500',
    defaultGuests: 25,
    defaultBudget: 1500,
    headline: 'Level Up Your 30th: The Ultimate Neon & Glow Aesthetic Blueprint',
    description: 'Transform your milestone birthday into a high-energy neon lounge. Ultraviolet ambiance, radiant typography, luminous glassware, and moody midnight backdrops that make electric pinks and cyber cyans explode.',
    palette: [
      { hex: '#FF007F', name: 'Electric Pink' },
      { hex: '#00F0FF', name: 'Cyber Cyan' },
      { hex: '#39FF14', name: 'Neon Lime' },
      { hex: '#7928CA', name: 'Ultraviolet' },
      { hex: '#0D0D15', name: 'Midnight Onyx' }
    ],
    highlights: [
      'Custom UV-reactive glowing cocktail bar with tonic blacklight effects',
      'Curated LED neon quote signs for viral Instagram & TikTok photo moments',
      'Sleek dark acrylic table runners with fluorescent florals and glow glassware',
      'DJ-inspired soundscape with hour-by-hour dancefloor transition cues'
    ],
    signatureSips: [
      { name: 'Cyber Blue Margarita', desc: 'Blue Curacao, Blanco Tequila, fresh lime, agave, and black salt rim' },
      { name: 'Dragonfruit Glow Fizz', desc: 'Dragonfruit puree, botanical gin, lemon, and chilled prosecco' },
      { name: 'Zero-Proof Neon Tonic', desc: 'Natural quinine tonic water (glows under UV), blackberry syrup, rosemary sprig' }
    ],
    foodPairings: [
      { name: 'Truffle Wagyu Sliders', desc: 'Mini brioche buns, aged white cheddar, caramelized onion jam' },
      { name: 'Gourmet Popcorn Station', desc: 'White truffle parmesan, sriracha lime, and sweet matcha dusted kernels' },
      { name: 'Glow Cotton Candy Champagne Flutes', desc: 'Pink spun sugar spun around LED swizzle sticks melted by sparkling wine' }
    ],
    decorChecklist: [
      { name: 'Custom LED Neon Script Sign ("Dirty Thirty" or "Let\'s Glow")', est: '$65 - $110' },
      { name: 'Blacklight UV Flood Light Bars (395nm wavelength)', est: '$35 - $60' },
      { name: 'Iridescent Clear Acrylic Bar Display Riser Kit', est: '$28 - $45' },
      { name: 'Glow-in-the-Dark Drink Tumblers & Highballs (Pack of 30)', est: '$24 - $38' },
      { name: 'Fluorescent Neon Velvet Ribbon & Hanging Cascades', est: '$18 - $30' }
    ],
    sampleTimeline: [
      { time: '7:00 PM', title: 'Guest Arrival & Cocktail Hour', desc: 'Ambient lounge beats, welcome signature sips, and UV body paint station.' },
      { time: '8:15 PM', title: 'Gourmet Bites & Mingling', desc: 'Heavy passed appetizers and interactive popcorn bar open.' },
      { time: '9:15 PM', title: 'Milestone Birthday Toast', desc: 'Lights dim to pure blacklight as the sparkler birthday cake arrives.' },
      { time: '9:45 PM', title: 'Dancefloor Ignites', desc: 'High energy playlist, glow accessories distributed to all guests.' },
      { time: '12:00 AM', title: 'Midnight Late-Night Snack', desc: 'Mini warm pretzel bites and churros before final send-off.' }
    ]
  },
  {
    slug: 'tuscan-sunset-dinner',
    occasion: 'Dinner Party / Milestone Celebration',
    title: 'Tuscan Sunset Vineyard Dinner Party: Outdoor Summer Soiree',
    metaDescription: 'Host a rustic Mediterranean al fresco dinner party with warm terracotta hues, olive branch runners, Italian wine pairings, and elegant linen place settings.',
    badge: 'Host Favorite',
    vibe: 'Rustic Mediterranean Romance & Golden Hour Warmth',
    themeName: 'Tuscan Golden Hour & Terracotta',
    archetype: 'Warm & Earthy',
    estimatedCostRange: '$800 – $1,800',
    defaultGuests: 16,
    defaultBudget: 1200,
    headline: 'Golden Hour Under the Stars: An Authentic Tuscan Evening',
    description: 'Bring the rolling hills of Tuscany to your backyard or dining hall. Unfinished wood farm tables, fragrant rosemary and olive branches, terracotta pottery, flickering taper candles, and leisurely multi-course dining.',
    palette: [
      { hex: '#C85A32', name: 'Terracotta' },
      { hex: '#556B2F', name: 'Olive Branch' },
      { hex: '#E3A857', name: 'Sunlit Ochre' },
      { hex: '#FDFBF7', name: 'Raw Linen' },
      { hex: '#58111A', name: 'Aged Chianti' }
    ],
    highlights: [
      'Family-style long farm table with continuous olive leaf and lemon garland',
      'Artisanal bread and olive oil flight tasting board on rustic stone slabs',
      'Muted terracotta bud vases with wildflower stems and dried wheat',
      'Acoustic Italian jazz and Mediterranean acoustic guitar background soundtrack'
    ],
    signatureSips: [
      { name: 'Blood Orange Aperol Spritz', desc: 'Prosecco, Aperol, splash of blood orange soda, fresh rosemary spear' },
      { name: 'Super Tuscan Sangiovese Flight', desc: 'Carefully curated tasting pours from Montalcino and Chianti Classico' },
      { name: 'Limoncello Basil Cooler', desc: 'Non-alcoholic botanical lemon press with bruised sweet basil and crushed ice' }
    ],
    foodPairings: [
      { name: 'Charcuterie & Burrata Board', desc: 'Prosciutto di Parma, figs, warm honeycomb, creamy Puglia burrata' },
      { name: 'Handmade Pappardelle al Cinghiale', desc: 'Slow braised ragu with wild mushroom and shaved Pecorino Romano' },
      { name: 'Olive Oil Citrus Polenta Cake', desc: 'Topped with mascarpone cream and candied orange peel' }
    ],
    decorChecklist: [
      { name: 'Italian Terracotta Flower Pots & Urns (Set of 6)', est: '$45 - $75' },
      { name: 'Natural Crinkle Flax Linen Table Runner (12 ft)', est: '$28 - $42' },
      { name: 'Hand-Dipped Olive & Rust Taper Candles (Box of 12)', est: '$22 - $35' },
      { name: 'Fresh Cut Olive Branch & Seeded Eucalyptus Stems', est: '$30 - $50' },
      { name: 'Stemless Crystal Wine Goblets (Set of 16)', est: '$38 - $60' }
    ],
    sampleTimeline: [
      { time: '6:30 PM', title: 'Golden Hour Apertivo', desc: 'Guests arrive for spritzes and olive oil tasting as the sun sets.' },
      { time: '7:30 PM', title: 'Seated Family-Style Feast', desc: 'Antipasti boards followed by warm pasta courses served under bistro string lights.' },
      { time: '9:00 PM', title: 'Dessert & Espresso Service', desc: 'Citrus cake, limoncello digestifs, and leisurely conversation.' },
      { time: '10:30 PM', title: 'Stargazing & Firepit Chat', desc: 'Casual winding down with vinyl records and cozy knit throws.' }
    ]
  },
  {
    slug: 'pastel-fairytale-baby-shower',
    occasion: 'Baby Shower',
    title: 'Enchanted Pastel Fairytale: Whimsical Baby Shower Blueprint',
    metaDescription: 'Create a dreamy fairytale baby shower with cloud-soft pastels, cascading floral clouds, butterfly touches, and French patisserie tea service.',
    badge: 'Gentle & Dreamy',
    vibe: 'Whimsical, Dreamy & Soft Pastels',
    themeName: 'Storybook Fairytale & Clouds',
    archetype: 'Soft & Romantic',
    estimatedCostRange: '$900 – $2,000',
    defaultGuests: 20,
    defaultBudget: 1100,
    headline: 'Once Upon a Time: A Breathtaking Storybook Celebration',
    description: 'Welcome the newest chapter with pure magic. Gossamer tulle drapery, pastel butterfly details, delicate French macarons, lush floral clouds in blush and sage, and vintage storybook placecards.',
    palette: [
      { hex: '#FFD1DC', name: 'Cloud Blush' },
      { hex: '#B0E0E6', name: 'Powder Blue' },
      { hex: '#C1E1C1', name: 'Soft Sage' },
      { hex: '#FFFDD0', name: 'Buttercream' },
      { hex: '#D4AF37', name: 'Gilded Gold' }
    ],
    highlights: [
      'Whimsical baby cloud balloon garland suspended above the gift lounge',
      'Vintage storybook "Wishes for Baby" guest keepsake signing station',
      'Tiered vintage afternoon tea trays with mini scones and floral teacups',
      'Classical harp and lofi fairytale playlist creating a serene, calm atmosphere'
    ],
    signatureSips: [
      { name: 'Sparkling Rose & Lavender Lemonade', desc: 'Fresh squeezed Meyer lemon with lavender blossom syrup and edible glitter' },
      { name: 'Baby Bellini Mocktail', desc: 'White peach nectar and chilled sparkling apple cider with raspberry garnish' },
      { name: 'Earl Grey Vanilla London Fog', desc: 'Steamed oat milk with microfoam and organic bergamot tea' }
    ],
    foodPairings: [
      { name: 'Artisan Tea Sandwiches', desc: 'Cucumber mint dill, smoked salmon chive cream, egg salad brioche' },
      { name: 'French Macaron Tower', desc: 'Pistachio, salted caramel, and Madagascar vanilla bean' },
      { name: 'Mini Fruit Tartlets', desc: 'Flaky buttery pastry filled with creme patissiere and glazed fresh berries' }
    ],
    decorChecklist: [
      { name: 'Pastel Matte Balloon Arch Cloud Kit (120 Pcs)', est: '$22 - $35' },
      { name: 'Gold Foil Scalloped Paper Plates & Napkins Set', est: '$19 - $30' },
      { name: '3D Laser-Cut Metallic Gold Butterfly Accents (Pack of 36)', est: '$12 - $18' },
      { name: 'Sheer Organza Table Drape & Swags (10 ft)', est: '$25 - $40' },
      { name: 'Hardcover Linen Storybook "Letters to You" Keepsake', est: '$20 - $32' }
    ],
    sampleTimeline: [
      { time: '11:00 AM', title: 'Welcome & Sip Reception', desc: 'Guests arrive to classical melody, mocktails, and guestbook signing.' },
      { time: '11:45 AM', title: 'Afternoon Tea Luncheon', desc: 'Delicate tiered sandwiches, fresh salads, and warm savories.' },
      { time: '12:45 PM', title: 'Low-Pressure Shower Games', desc: 'Baby book trivia and nursery rhyme matching without embarrassing the parents.' },
      { time: '1:30 PM', title: 'Sweet Treat Toast & Gift Opening', desc: 'Macaron tower reveal, champagne/cider toast to the parents-to-be.' }
    ]
  },
  {
    slug: 'retro-disco-fever',
    occasion: 'Milestone Birthday / New Year\'s Eve',
    title: 'Studio 54 Retro Disco Fever: 70s Glam Celebration Guide',
    metaDescription: 'Throw the ultimate 70s Studio 54 retro disco dance party. Mirror ball centerpieces, metallic silver accents, bell-bottom cocktails, and funky groove lighting.',
    badge: 'Party All Night',
    vibe: 'Mirrorball Glamour & Vintage Chic',
    themeName: 'Midnight Disco & Stardust',
    archetype: 'Bold & Vibrant',
    estimatedCostRange: '$1,000 – $2,200',
    defaultGuests: 30,
    defaultBudget: 1400,
    headline: 'Get Down Tonight: Pure Studio 54 Dance Energy',
    description: 'Nothing beats disco glamour. Multi-sized mirrorballs that shower the room in rotating starlight beams, velvet lounge corners, retro metallic tinsel fringe, and an infectious 70s funk soundtrack that keeps everyone dancing.',
    palette: [
      { hex: '#C0C0C0', name: 'Mirrorball Silver' },
      { hex: '#FFD700', name: 'Liquid Gold' },
      { hex: '#C71585', name: 'Velvet Magenta' },
      { hex: '#008080', name: 'Electric Teal' },
      { hex: '#121212', name: 'Deep Space' }
    ],
    highlights: [
      'Cluster of 5 hanging disco balls of varied sizes creating an electric focal ceiling',
      'Shimmering silver foil fringe backdrop wall for photobooth snapshots',
      'Vintage vinyl record chargers under metallic dinner plates',
      'Funk, Nu-Disco, and classic Earth Wind & Fire playlist'
    ],
    signatureSips: [
      { name: 'The Golden Glamour Disco Sour', desc: 'Bourbon, fresh lemon, honey liqueur, with a shimmering edible gold dust swirl' },
      { name: 'Teal Roller-Skate Punch', desc: 'White rum, blue curacao, pineapple juice, coconut water, and maraschino cherry' },
      { name: 'Sparkling Stardust Mocktail', desc: 'Ginger beer, pomegranate juice, lime, and silver food-safe glitter' }
    ],
    foodPairings: [
      { name: 'Retro Fondue Station', desc: 'Warm Gruyere & Emmental cheese with sourdough cubes and crisp apples' },
      { name: 'Glazed Cocktail Meatball Skewers', desc: 'Sweet chili cranberry reduction with chives' },
      { name: 'Silver Dusted Chocolate Truffles', desc: 'Dark espresso ganache rolled in shimmering silver cocoa' }
    ],
    decorChecklist: [
      { name: 'Assorted Mirrored Glass Disco Balls (8", 10", 12")', est: '$45 - $80' },
      { name: 'Metallic Silver Tinsel Fringe Curtains (3-Pack)', est: '$16 - $25' },
      { name: 'Rotating Color Pinspot Light with Remote', est: '$29 - $48' },
      { name: 'Disco Ball Cocktail Cups with Straws (Set of 24)', est: '$28 - $42' },
      { name: 'Glittering Silver Starburst Foil Balloon Clusters', est: '$18 - $28' }
    ],
    sampleTimeline: [
      { time: '8:00 PM', title: 'Cocktails & Groovy Beats', desc: 'Guests arrive dressed in sequins, flares, and velvet.' },
      { time: '9:00 PM', title: 'Fondue Lounge & Socializing', desc: 'Retro cocktail bites served as the mirrorballs spin.' },
      { time: '10:00 PM', title: 'Dancefloor Takeover', desc: 'Soundtrack peaks with Studio 54 disco anthems.' },
      { time: '12:30 AM', title: 'Late Night Diner Fries & Shake Shots', desc: 'Salty midnight snacks for exhausted dancers.' }
    ]
  },
  {
    slug: 'coastal-grandmother-brunch',
    occasion: 'Bridal Shower / Birthday Brunch',
    title: 'Coastal Hydrangea & Rosé: Elegant Seaside Garden Brunch',
    metaDescription: 'Host a dreamy coastal garden brunch inspired by seaside elegance. Blue hydrangeas, crisp French linen, chilled rosé towers, and gourmet artisanal pastries.',
    badge: 'Effortless Chic',
    vibe: 'Effortless Seaside Luxury & Crisp Linens',
    themeName: 'Nantucket Breeze & Hydrangeas',
    archetype: 'Fresh & Airy',
    estimatedCostRange: '$750 – $1,600',
    defaultGuests: 18,
    defaultBudget: 950,
    headline: 'Breezy Seaside Elegance: Hydrangeas, Linen & Rosé',
    description: 'Chic, relaxed, and impossibly charming. Think coastal New England meets French Riviera: overflowing arrangements of blue hydrangeas, woven rattan chargers, sun-dappled patio dining, and crisp Provence rosé.',
    palette: [
      { hex: '#6495ED', name: 'Hydrangea Blue' },
      { hex: '#F8F9FA', name: 'Sea Salt White' },
      { hex: '#002D62', name: 'Crisp Navy' },
      { hex: '#967BB6', name: 'French Lavender' },
      { hex: '#D2B48C', name: 'Sandbar Tan' }
    ],
    highlights: [
      'Potted and cut fresh blue & white hydrangeas down the center runner',
      'Woven rattan placemats paired with classic blue gingham napkins',
      'Chilled Provence rosé flight accompanied by sparkling San Pellegrino',
      'Bossa nova and acoustic coastal guitar playlist'
    ],
    signatureSips: [
      { name: 'Seaside St-Germain Spritz', desc: 'Elderflower liqueur, dry prosecco, club soda, lemon twist, fresh mint' },
      { name: 'Provence Chilled Rosé', desc: 'Pale salmon pink, dry and crisp with notes of white peach and strawberry' },
      { name: 'Cucumber Rosemary Spa Sparkler', desc: 'Muddled English cucumber, lime juice, rosemary simple syrup, soda' }
    ],
    foodPairings: [
      { name: 'Mini Lobster Brioche Rolls', desc: 'Chilled Maine lobster, light lemon herb mayo, butter toasted slider buns' },
      { name: 'Heirloom Tomato & Whipped Ricotta Toast', desc: 'Sourdough, balsamic glaze, micro basil' },
      { name: 'Citrus Glazed Brioche Cinnamon Swirls', desc: 'Flaky French pastry fresh from the oven' }
    ],
    decorChecklist: [
      { name: 'Natural Round Woven Rattan Chargers (Set of 8)', est: '$32 - $50' },
      { name: 'French Navy Gingham Cloth Napkins (Set of 12)', est: '$22 - $34' },
      { name: 'Ribbed Vintage Glass Pitchers for Spritzes', est: '$24 - $38' },
      { name: 'Ceramic White Scalloped Serving Platters', est: '$28 - $45' },
      { name: 'Faux / Fresh Blue Hydrangea Stems Bouquet', est: '$30 - $48' }
    ],
    sampleTimeline: [
      { time: '11:00 AM', title: 'Sunlit Welcome & Rosé Pour', desc: 'Guests arrive to gentle ocean breeze acoustics and chilled welcome spritzes.' },
      { time: '11:45 AM', title: 'Al Fresco Brunch Service', desc: 'Warm savory pastries, lobster rolls, and fresh seasonal fruit.' },
      { time: '1:00 PM', title: 'Coffee & Dessert Lounge', desc: 'Artisanal espresso and lavender madeleines.' },
      { time: '2:30 PM', title: 'Farewell Flower Bouquet Wrapping', desc: 'Guests take home mini hydrangea bouquets wrapped in brown kraft paper.' }
    ]
  },
  {
    slug: 'whimsical-woodland-adventure',
    occasion: 'Kids Birthday / 1st Birthday',
    title: 'Wild Woodland Explorer: Magical Forest Adventure Party',
    metaDescription: 'Plan an enchanting woodland explorer kids party with moss green details, woodland animal treats, campfire smores, and nature scavenger hunts.',
    badge: 'Family Favorite',
    vibe: 'Enchanted Forest, Cozy Moss & Campfire Fun',
    themeName: 'Enchanted Woodland Wildlife',
    archetype: 'Warm & Earthy',
    estimatedCostRange: '$600 – $1,300',
    defaultGuests: 20,
    defaultBudget: 750,
    headline: 'Into the Woods: A Magical Nature Adventure',
    description: 'Immerse little adventurers in a lush, storybook forest. Faux moss table runners, wood slice cake stands, cute woodland creatures (foxes, bears, owls), mini canvas teepees, and campfire-themed treats.',
    palette: [
      { hex: '#4A5D23', name: 'Moss Green' },
      { hex: '#1E3F20', name: 'Pine Forest' },
      { hex: '#8B5A2B', name: 'Acorn Brown' },
      { hex: '#7FA054', name: 'Wild Fern' },
      { hex: '#F5F2EB', name: 'Birch Bark' }
    ],
    highlights: [
      'Natural wood tree slab dessert risers and pine cone table accents',
      'Cozy canvas play teepee reading and quiet zone with plush forest animals',
      'Nature Explorer Scavenger Hunt with personalized magnifying glasses',
      'Gentle acoustic folk and whimsical instrumental movie soundtracks'
    ],
    signatureSips: [
      { name: 'Campfire Cocoa Bar', desc: 'Rich hot chocolate with mini marshmallows, cinnamon sticks, and whipped cream' },
      { name: 'Wild Berry Forest Punch', desc: 'Apple juice, cranberry, sparkling water, and floating blackberries' },
      { name: 'Bear Cub Apple Cider', desc: 'Chilled spiced apple cider with orange slices' }
    ],
    foodPairings: [
      { name: 'Gourmet S\'mores Dip Bar', desc: 'Baked skillet chocolate and marshmallows with graham cracker dippers' },
      { name: 'Woodland Animal Cupcakes', desc: 'Fondant fox, owl, and hedgehog toppers on vanilla bean cupcakes' },
      { name: 'Trail Mix Explorer Station', desc: 'Pretzel sticks ("twigs"), yogurt raisins ("river pebbles"), and dried fruit' }
    ],
    decorChecklist: [
      { name: 'Preserved Faux Moss Table Runner (6 ft)', est: '$18 - $28' },
      { name: 'Natural Rustic Wood Slice Centerpieces (Pack of 6)', est: '$24 - $38' },
      { name: 'Woodland Creature Cardstock Cake & Food Toppers', est: '$12 - $18' },
      { name: 'Kids Canvas Teepee Play Tent', est: '$35 - $55' },
      { name: 'Mini Wooden Magnifying Glasses & Explorer Badges', est: '$16 - $24' }
    ],
    sampleTimeline: [
      { time: '2:00 PM', title: 'Explorers Assemble', desc: 'Guests arrive, receive explorer vests or badges, and enjoy trail mix.' },
      { time: '2:30 PM', title: 'Woodland Scavenger Hunt', desc: 'Guided outdoor or indoor treasure hunt searching for hidden wooden forest creatures.' },
      { time: '3:30 PM', title: 'Campfire S\'mores & Birthday Cake', desc: 'Gather around the wood slice cake table for singing and candles.' },
      { time: '4:15 PM', title: 'Teepee Storytime & Goodbyes', desc: 'Cozy wind-down reading an adventure book, departing with seedling plant party favors.' }
    ]
  }
];

export const getOccasionBySlug = (slug) => {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return OCCASION_PAGES.find(p => p.slug.toLowerCase() === normalized) || null;
};
